import { apiRequest } from './client';
import type {
  SalesforceOrg,
  SalesforceAuth,
  SalesforceRouting,
  OrgConnectionBinding,
  SalesforceMetadataItem,
  CreateSalesforceOrgInput,
  UpdateSalesforceOrgInput,
  UpdateSalesforceAuthInput,
  UpdateSalesforceRoutingInput,
  BindTelephonyInput,
  OrgTelephonyDefaultsInput,
  UUID,
} from '@/types';

export const salesforceApi = {
  listOrgs(tenantId: UUID): Promise<SalesforceOrg[]> {
    return apiRequest(`/api/v1/tenants/${tenantId}/salesforce-orgs`, { tenantId });
  },

  getOrg(tenantId: UUID, orgId: UUID): Promise<SalesforceOrg> {
    return apiRequest(`/api/v1/tenants/${tenantId}/salesforce-orgs/${orgId}`, { tenantId });
  },

  createOrg(tenantId: UUID, input: CreateSalesforceOrgInput): Promise<SalesforceOrg> {
    return apiRequest(`/api/v1/tenants/${tenantId}/salesforce-orgs`, {
      method: 'POST',
      body: input,
      tenantId,
    });
  },

  updateOrg(tenantId: UUID, orgId: UUID, input: UpdateSalesforceOrgInput): Promise<SalesforceOrg> {
    return apiRequest(`/api/v1/tenants/${tenantId}/salesforce-orgs/${orgId}`, {
      method: 'PATCH',
      body: input,
      tenantId,
    });
  },

  getAuth(tenantId: UUID, orgId: UUID): Promise<SalesforceAuth> {
    return apiRequest(`/api/v1/tenants/${tenantId}/salesforce-orgs/${orgId}/auth`, { tenantId });
  },

  updateAuth(tenantId: UUID, orgId: UUID, input: UpdateSalesforceAuthInput): Promise<SalesforceAuth> {
    return apiRequest(`/api/v1/tenants/${tenantId}/salesforce-orgs/${orgId}/auth`, {
      method: 'PUT',
      body: input,
      tenantId,
    });
  },

  testAuth(tenantId: UUID, orgId: UUID): Promise<{ success: boolean; message: string }> {
    return apiRequest(`/api/v1/tenants/${tenantId}/salesforce-orgs/${orgId}/auth/test`, {
      method: 'POST',
      tenantId,
    });
  },

  getRouting(tenantId: UUID, orgId: UUID): Promise<SalesforceRouting> {
    return apiRequest(`/api/v1/tenants/${tenantId}/salesforce-orgs/${orgId}/routing`, { tenantId });
  },

  updateRouting(
    tenantId: UUID,
    orgId: UUID,
    input: UpdateSalesforceRoutingInput
  ): Promise<SalesforceRouting> {
    return apiRequest(`/api/v1/tenants/${tenantId}/salesforce-orgs/${orgId}/routing`, {
      method: 'PUT',
      body: input,
      tenantId,
    });
  },

  discoverCallCenters(tenantId: UUID, orgId: UUID): Promise<SalesforceMetadataItem[]> {
    return apiRequest(
      `/api/v1/tenants/${tenantId}/salesforce-orgs/${orgId}/routing/discover/call-centers`,
      { tenantId }
    );
  },

  discoverQueues(tenantId: UUID, orgId: UUID): Promise<SalesforceMetadataItem[]> {
    return apiRequest(
      `/api/v1/tenants/${tenantId}/salesforce-orgs/${orgId}/routing/discover/queues`,
      { tenantId }
    );
  },

  discoverVoiceFlows(tenantId: UUID, orgId: UUID): Promise<SalesforceMetadataItem[]> {
    return apiRequest(
      `/api/v1/tenants/${tenantId}/salesforce-orgs/${orgId}/routing/discover/voice-flows`,
      { tenantId }
    );
  },

  listBindings(tenantId: UUID, orgId: UUID): Promise<OrgConnectionBinding[]> {
    return apiRequest(`/api/v1/tenants/${tenantId}/salesforce-orgs/${orgId}/bindings`, { tenantId });
  },

  createBinding(tenantId: UUID, orgId: UUID, input: BindTelephonyInput): Promise<OrgConnectionBinding> {
    return apiRequest(`/api/v1/tenants/${tenantId}/salesforce-orgs/${orgId}/bindings`, {
      method: 'POST',
      body: input,
      tenantId,
    });
  },

  updateBindingDefaults(
    tenantId: UUID,
    orgId: UUID,
    input: OrgTelephonyDefaultsInput
  ): Promise<OrgConnectionBinding[]> {
    return apiRequest(`/api/v1/tenants/${tenantId}/salesforce-orgs/${orgId}/bindings/defaults`, {
      method: 'PUT',
      body: input,
      tenantId,
    });
  },

  syncFromSalesforce(tenantId: UUID, orgId: UUID): Promise<void> {
    return apiRequest(`/api/v1/tenants/${tenantId}/salesforce-orgs/${orgId}/sync`, {
      method: 'POST',
      tenantId,
    });
  },
};
